import { TZDate } from "@date-fns/tz";
import Humanize from "humanize-plus";
import zxcvbn from "zxcvbn";
import UserRoles, { Role } from "../constants/roles";
import { User } from "../context/UserContext";

import { format, isThisYear, parseISO } from "date-fns";
import { DataGridSortableColumns, SortDirection } from "../graphql/types";
import { OptionalRange } from "../types";

export const exists = (val: number | string | object | null | undefined) => val !== undefined && val !== null;

export const isEmptyObject = (val: object) => typeof val === "object" && Object.keys(val).length === 0;

export const isDate = (src: string | number | Date) => {
  return src ? !isNaN(new Date(src).getDate()) : false;
};

export const toDefaultDate = (isoDate: string | number | Date) => {
  if (isDate(isoDate)) {
    const dte = new Date(sanitizeIsoformat(isoDate.toString()));
    if (isThisYear(dte)) {
      return format(new Date(dte), "MMM d, h:mmaaa");
    }
    return format(new Date(dte), "MMM d, yyyy h:mmaaa");
  }
};

export const toDefaultBytes = (val: number) => {
  if (!isNaN(val)) {
    return Humanize.fileSize(val);
  }
};

export const toDefaultInt = (val: number) => {
  if (!isNaN(val)) {
    return Humanize.compactInteger(val, 1);
  }
};

export const toLocalAndUtcDates = (isoformat: string) => {
  const srcDate = sanitizeIsoformat(isoformat);
  const srcDte = new Date(srcDate);
  const browser = format(srcDte, "EEE MMM d yyyy HH:mm z");
  const utcDate = new TZDate(srcDate, "UTC");
  const utc = `${format(utcDate, "EEE MMM d yyyy HH:mm")} UTC`;
  const date = format(utcDate, "EEE MMM d yyyy");
  const time = `${format(utcDate, "HH:mm")} UTC`;
  return { browser, utc, date, time };
};

export const sanitizeIsoformat = (src: string) => {
  if (src) {
    src = `${src.substring(0, 19)}Z`;
  }
  return src;
};

export const isAdminScreen = (hash: string) => {
  hash = hash || window.location.hash; // "#/app/example.com/downloads/list"
  return hash.indexOf("#/admin/") > -1;
};

export const getUrlName = (hash?: string) => {
  hash = hash || window.location.hash; // "#/app/example.com/downloads/list"
  const pathItems = hash.split("/");
  const appIndex = pathItems.findIndex((item) => item === "app");
  if (appIndex > -1 && appIndex < pathItems.length) {
    const url_name = pathItems[appIndex + 1] || "";
    if (!url_name || url_name.length === 0) {
      return null;
    }
    return url_name;
  } else {
    return null;
  }
};

export const getQueryParams = (search: string) => {
  const result: Record<string, string> = {};
  if (search) {
    const params = new URLSearchParams(search);
    params.forEach((value, key) => {
      result[key] = value;
    });
  }
  return result;
};

// https://stackoverflow.com/questions/18936915/dynamically-set-property-of-nested-object
export const setObjectField = <T = unknown>(obj: Record<string, unknown>, path: string, value: T) => {
  const pList = path.split(".");
  const len = pList.length;
  for (let i = 0; i < len - 1; i++) {
    // If pList[i] looks like a number, treat it like a number rather than a string.
    const elem = isNaN(Number(pList[i])) ? pList[i] : Number(pList[i]);
    if (!obj[elem]) obj[elem] = {};
    obj = obj[elem] as Record<string, unknown>;
  }

  obj[pList[len - 1]] = value;
};

export interface NestedObject {
  [key: string]: string | NestedObject;
}

// https://stackoverflow.com/questions/8817394/javascript-get-deep-value-from-object-by-passing-path-to-it-as-string
export const getObjectField = (
  obj: NestedObject | string | Error | unknown,
  path: string
): object | string | number | boolean | undefined | null => {
  if (obj === undefined || obj === null) {
    return obj;
  }
  const paths = path.split(".");
  let current: unknown = obj;

  for (let i = 0; i < paths.length; ++i) {
    const next = (current as Record<string, unknown>)[paths[i]];
    if (next === undefined) {
      return undefined;
    }

    if (next === null) {
      return null;
    }

    current = next;
  }
  return current as object | string | number | boolean;
};

export const pluralize = (word: string, total: number) => {
  if (word === "is") {
    return total !== 1 ? "are" : "is";
  }
  return `${word}${total !== 1 ? "s" : ""}`;
};

export const isNullOrUndefined = <T = unknown>(val: T): val is T & (null | undefined) =>
  val === null || val === undefined;

export const toSnakeCase = (s: string) => {
  return s.replace(/[_-]([a-z])/gi, (m: string) => m[1].toUpperCase());
};

interface ParamsPropsCreateUrl {
  [key: string]: string | number | boolean | undefined;
}

export const toCreateUrl = (path?: string | Array<string>, params?: ParamsPropsCreateUrl) => {
  if (typeof path === "string") {
    path = path.split("/");
  }
  // If we have parts of the path that need encoding supply path as an array.  This works for the editor type links
  // where the SBS filename becomes part of the url and can have special characters.
  const path_enc = path?.map((p) => encodeURIComponent(p)).join("/");

  if (params && typeof params === "object" && Object.keys(params).length > 0) {
    const params_enc_arr: string[] = [];
    for (const [key, value] of Object.entries(params)) {
      if (value) {
        params_enc_arr.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }
    return `${path_enc}?${params_enc_arr.join("&")}`;
  } else {
    return `${path_enc}`;
  }
};

export const handleInfiniteScroll = (
  { currentTarget }: { currentTarget: HTMLElement },
  onLoadMore: () => void,
  isPageable: boolean
) => {
  // Provide a bit of easing on the scroll height in order to accommodate across browsers and screens.
  const easing = 100;
  if (isPageable && currentTarget.scrollTop + currentTarget.clientHeight + easing >= currentTarget.scrollHeight) {
    onLoadMore();
  }
};

export const getUserInitials = (name: string): string => {
  const initials = name
    .split(" ")
    .filter(Boolean) // Handle extra spaces
    .map((part) => part[0].toUpperCase())
    .slice(0, 2)
    .join("");
  return initials;
};

export const isValidUrlName = (user: User, urlName: string) => {
  Object.keys(user?.accounts).forEach((account_url) => {
    if (account_url === urlName) return true;
  });

  return false;
};
export enum OS_TYPES {
  Mac = "Mac",
  Other = "Other",
}

export const getOSType = () => {
  if (window.navigator.userAgent.indexOf("Mac") !== -1) {
    return OS_TYPES.Mac;
  } else {
    return OS_TYPES.Other;
  }
};

export const toTitleCase = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const validateEmail = (email: string | string[] | null | undefined) => {
  if (email === "") return "This field is required";
  const re =
    // eslint-disable-next-line no-useless-escape
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  if (!re.test(String(email).toLowerCase())) {
    return "The email is invalid";
  }
};

export const getAllowedRoles = (roles: string[]) => {
  const allowedRoles: Role[] = [];
  if (roles.includes("tester")) {
    (Object.keys(UserRoles) as (keyof typeof UserRoles)[]).forEach((key) => {
      allowedRoles.push(UserRoles[key].value);
    });
  } else if (roles.includes("cs")) {
    allowedRoles.push(UserRoles.cs.value);
    allowedRoles.push(UserRoles.operator.value);
    allowedRoles.push(UserRoles.viewer.value);
    allowedRoles.push(UserRoles.developer.value);
    allowedRoles.push(UserRoles.beta_backfill.value);
  } else if (roles.includes("operator")) {
    allowedRoles.push(UserRoles.operator.value);
    allowedRoles.push(UserRoles.viewer.value);
  } else if (roles.includes("viewer")) {
    allowedRoles.push(UserRoles.viewer.value);
  }
  return allowedRoles;
};

export const VALID_SPECIAL_CHARS_REGEX = /[!@#$%^&*()_+\-=[\]{}|;:,.<>?/~]/;
const INVALID_PASSWORD_CHARS_REGEX = /[^a-zA-Z\d!@#$%^&*()_+\-=[\]{}|;:,.<>?/~ ]/g;
// This method must match the one in sb1/db/accounts.py
export const validatePasswordPolicy = (password: string) => {
  // Policy check
  const policyTest: string[] = [];
  if (password.length < 8 || password.length > 128) {
    policyTest.push("8 to 128 characters");
  }
  if (!/[a-z]/.test(password)) {
    policyTest.push("1 lowercase character");
  }
  if (!/[A-Z]/.test(password)) {
    policyTest.push("1 uppercase character");
  }
  if (!/\d/.test(password)) {
    policyTest.push("1 digit");
  }
  if (!VALID_SPECIAL_CHARS_REGEX.test(password)) {
    policyTest.push(`1 special character from "${VALID_SPECIAL_CHARS_REGEX.source}"`);
  }
  if (INVALID_PASSWORD_CHARS_REGEX.test(password)) {
    policyTest.push(`No special characters from ["${VALID_SPECIAL_CHARS_REGEX.source}"]`);
  }
  let errors = policyTest.length > 0 ? `Password must have: ${policyTest.join(", ")}.` : "";

  // Strength check
  const strengthTest = zxcvbn(password);
  let feedback = "";
  const score = strengthTest.score;
  if (score <= 2) {
    let msg = "Password isn't strong enough.";
    if (strengthTest.feedback.warning) {
      msg = `${msg} ${strengthTest.feedback.warning}`;
    }
    const suggestions = strengthTest.feedback.suggestions || [];
    if (suggestions.length > 0) {
      msg = `${msg} ${suggestions.join(" ")}`;
    }
    errors = errors.concat(msg);
    feedback = msg;
  }
  return { errors, score, feedback };
};

export const getCurrentUtcDate = () => {
  const today = new Date();
  const currentUtcDate = new Date(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  return currentUtcDate;
};

export const getFormattedDate = (date: string | null) => {
  if (!date) {
    return "";
  }
  const formattedDate = isThisYear(new Date(date))
    ? format(new TZDate(date, "UTC"), "MMM dd")
    : format(new TZDate(date, "UTC"), "MMM dd, yyyy");
  return formattedDate;
};

const formatReportRange = (isoDate: string) => {
  const [datePart, timePart] = isoDate.split("T");
  const [hourPart, rawOffset] = timePart.split(timePart.includes("+") ? "+" : "-");
  const [offsetHours = "0", offsetMinutes] = rawOffset?.split(":") || [];
  const offsetSign = timePart.includes("-") ? "-" : "+";
  const offset = ` (${offsetSign}${Number(offsetHours)}${
    offsetMinutes && offsetMinutes !== "00" ? `:${offsetMinutes}` : ""
  })`;

  const parsedDate = parseISO(`${datePart}T${hourPart}`);

  const formattedIsoDate = format(parsedDate, "MMM dd, h:mmaaa");

  const formattedDate = `${formattedIsoDate}${offset}`;

  return formattedDate;
};

export const getFormattedReportDate = (isoDate?: string | null, showTime?: boolean) => {
  if (!isoDate) {
    return "";
  }
  if (showTime) {
    return formatReportRange(isoDate);
  }

  return isoDate.split("T")[0];
};

export const humanizeNumber = (value: number) => {
  if (value > 1000000000) {
    return `${(value / 1000000000).toFixed(1)}B`;
  }
  if (value > 1000000) {
    return `${(value / 1000000).toFixed(1)}M`;
  }
  if (value > 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return value.toString();
};

export const snakeCaseToCapitalized = (str: string) => {
  return str.replace(/_/g, " ").replace(/\b\w/g, (l: string) => l.toUpperCase());
};

export const snakeCaseToCamelCase = (str: string) => {
  return str.replace(/_./g, (x: string) => x[1].toUpperCase());
};

export const dummyPassword = "••••••••••••••••••••••";

export const calculateDateDuration = (started?: string | null, finished?: string | null) => {
  if (!started || !finished) {
    return "";
  }
  const startedDate = new Date(started);
  const finishedDate = new Date(finished);
  if (!isNaN(startedDate.getTime()) && !isNaN(finishedDate.getTime())) {
    const diff = Math.abs(finishedDate.getTime() - startedDate.getTime());
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    let duration = "";
    if (hours > 0) duration += `${hours}h `;
    if (minutes > 0) duration += `${minutes}m `;
    if (seconds > 0) duration += `${seconds}s`;
    if (hours === 0 && minutes === 0 && seconds === 0) duration = "0s";
    return duration.trim();
  }
  return "";
};

export const setOrRemoveSearchParam = (
  key: string,
  value: string | SortDirection | DataGridSortableColumns | undefined,
  updatedParams: URLSearchParams
) => {
  if (value) {
    updatedParams.set(key, value);
  } else {
    updatedParams.delete(key);
  }
};

export const setOrRemoveRangeParam = (
  key1: string,
  key2: string,
  updatedParams: URLSearchParams,
  range?: OptionalRange<Date>
) => {
  if (range?.start && range?.end) {
    setOrRemoveSearchParam(key1, format(range?.start, "yyyy-MM-dd"), updatedParams);
    setOrRemoveSearchParam(key2, format(range?.end, "yyyy-MM-dd"), updatedParams);
  } else {
    updatedParams.delete(key1);
    updatedParams.delete(key2);
  }
};

export const isDateWithinRange = (actualDate: Date, start: Date, end: Date): boolean => {
  const endDate = new Date(end);
  endDate.setHours(23, 59, 59, 999);

  return actualDate >= start && actualDate < endDate;
};

export const toDayDate = (dateString: string) => {
  const [year, month, day] = dateString.split("T")[0].split("-").map(Number);
  return new Date(year, month - 1, day);
};
